/**
 * The approval card (slice 3 §8; boards AP1–AP4): what the crew drafted,
 * filled by the Worker from the held call, and the approver's Approve and
 * Change it. It renders live from the thread's `approvals`, so it changes in
 * place for everyone: open (the approver's buttons, everyone else's "Waiting
 * on"), sending, and closed, shrunk to one line. An email is an editable
 * letter: what the card shows is what's sent.
 */
import { Button } from "@crew/ui/components/button";
import { cn } from "@crew/ui/lib/utils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Check,
  CircleAlert,
  Clock,
  Loader2,
  Mail,
  Pencil,
  Undo2,
  X,
} from "lucide-react";
import { useState, type KeyboardEvent, type ReactNode } from "react";
import { errorHint } from "../../app/api";
import { meKey, type Me } from "../../app/me";
import { threadKey, threadsKey } from "../chats/api";
import { shortTime } from "../chats/format";
import { approve, decline, type Approval, type EmailEdits, type Preview } from "./api";
import {
  approveLabel,
  closedLine,
  dateParts,
  eventWhen,
  titleWith,
  firstName,
  footNote,
  sameList,
  splitAddresses,
} from "./approval-view";

const EYEBROW = "text-xs leading-4 font-semibold tracking-[0.06em] uppercase text-copper";
const SHELL =
  "flex flex-col gap-4 rounded-panel bg-card p-4 shadow-[0_0_0_1px_var(--color-border),0_1px_2px_#1F2A440A] @[480px]:p-5";

/** After Approve or Change it: the card, the chat, the top of Chats, the sidebar's count and the task. */
function useRefresh(a: Approval) {
  const client = useQueryClient();
  return () => {
    for (const key of [threadKey(a.thread_id), threadsKey, meKey, ["approvals"]]) {
      void client.invalidateQueries({ queryKey: key });
    }
    if (a.task) void client.invalidateQueries({ queryKey: ["task", a.task.id] });
  };
}

/** The card's eyebrow: "Approve · Gmail", or a routine's. */
const eyebrowOf = (a: Approval) =>
  a.kind === "routine" ? "Approve · New routine" : `Approve · ${a.action?.app_name ?? "App"}`;

// ── the letter (AP1) ─────────────────────────────────────────────────────

type Slot = "to" | "cc" | "bcc";

/**
 * The letter as the approver has it. `typing`: what's in an address field
 * not yet made a chip; it counts, so Approve sends what the field shows even
 * before Enter (Devin on #491).
 */
type Draft = {
  to: string[];
  cc: string[];
  bcc: string[];
  subject: string;
  body: string;
  typing: Record<Slot, string>;
};

const draftOf = (p: Extract<Preview, { layout: "email" }>): Draft => ({
  to: p.to,
  cc: p.cc,
  bcc: p.bcc,
  subject: p.subject ?? "",
  body: p.body ?? "",
  typing: { to: "", cc: "", bcc: "" },
});

/** A slot's addresses with what's being typed in it. */
const withTyping = (d: Draft, k: Slot) => [
  ...d[k],
  ...splitAddresses(d.typing[k]).filter((x) => !d[k].includes(x)),
];

/** Only what changed, in the route's shape (slice 3 §6). */
function editsOf(from: Draft, now: Draft): EmailEdits | undefined {
  const edits: EmailEdits = {};
  for (const k of ["to", "cc", "bcc"] as const) {
    const list = withTyping(now, k);
    if (!sameList(from[k], list)) edits[k] = list;
  }
  if (from.subject !== now.subject) edits.subject = now.subject;
  if (from.body !== now.body) edits.body = now.body;
  return Object.keys(edits).length ? edits : undefined;
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex min-h-10 items-center gap-3 border-b border-border py-1.5 last:border-0">
      <span className="w-14 shrink-0 text-meta text-hint">{label}</span>
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5 text-sm text-foreground">
        {children}
      </div>
    </div>
  );
}

/**
 * Addresses as chips, with a field that adds one on Enter or a comma. What's
 * left in it counts as typed (the draft's `typing`): not made a chip on
 * leaving, so the row doesn't reflow under a tap on Approve (CI at 390).
 */
function Addresses({
  label,
  value,
  onChange,
  typing,
  setTyping,
  editable,
  placeholder,
}: {
  label: string;
  value: string[];
  /** The chips, and what's left in the field. */
  onChange: (next: string[], typing?: string) => void;
  typing: string;
  setTyping: (typing: string) => void;
  editable: boolean;
  placeholder: string;
}) {
  const add = () => {
    const more = splitAddresses(typing);
    onChange([...value, ...more.filter((x) => !value.includes(x))], "");
  };
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      add();
    }
    if (e.key === "Backspace" && !typing && value.length) onChange(value.slice(0, -1));
  };
  return (
    <Row label={label}>
      {value.map((addr) => (
        <span
          key={addr}
          className="inline-flex items-center gap-1 rounded-tag bg-secondary px-2 py-0.5 text-sm"
        >
          {addr}
          {editable ? (
            <button
              type="button"
              aria-label={`Remove ${addr}`}
              onClick={() => onChange(value.filter((x) => x !== addr))}
              className="-mr-1 rounded p-0.5 text-hint hover:text-foreground"
            >
              <X className="size-3" />
            </button>
          ) : null}
        </span>
      ))}
      {editable ? (
        <input
          aria-label={`${label}: add someone`}
          value={typing}
          onChange={(e) => setTyping(e.target.value)}
          onKeyDown={onKey}
          placeholder={value.length ? "" : placeholder}
          className={cn(
            "flex-1 bg-transparent text-base text-foreground outline-none placeholder:text-hint md:text-sm",
            value.length ? "min-w-10" : "min-w-24",
          )}
        />
      ) : value.length ? null : (
        <span className="text-hint">None</span>
      )}
    </Row>
  );
}

/** Which address rows show: To always; Cc and Bcc when they can be added to or have someone. */
const ADDRESS_ROWS = [
  { slot: "to", label: "To", shows: () => true },
  { slot: "cc", label: "Cc", shows: (can: boolean, n: number) => can || n > 0 },
  { slot: "bcc", label: "Bcc", shows: (can: boolean, n: number) => can || n > 0 },
] as const;

function SubjectRow({
  draft,
  setDraft,
  editable,
}: {
  draft: Draft;
  setDraft: (d: Draft) => void;
  editable: boolean;
}) {
  return (
    <Row label="Subject">
      {editable ? (
        <input
          aria-label="Subject"
          value={draft.subject}
          onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
          className="w-full bg-transparent text-base font-medium text-foreground outline-none md:text-sm"
        />
      ) : (
        <span className="font-medium">{draft.subject}</span>
      )}
    </Row>
  );
}

function BodyField({
  draft,
  setDraft,
  editable,
}: {
  draft: Draft;
  setDraft: (d: Draft) => void;
  editable: boolean;
}) {
  if (!editable) {
    return (
      <p className="py-3 text-sm leading-[22px] whitespace-pre-wrap text-foreground">
        {draft.body}
      </p>
    );
  }
  return (
    <textarea
      aria-label="Email body"
      value={draft.body}
      onChange={(e) => setDraft({ ...draft, body: e.target.value })}
      rows={Math.min(18, Math.max(5, draft.body.split("\n").length + 1))}
      className="-mx-2 my-2 block w-[calc(100%+1rem)] resize-y rounded-md bg-transparent px-2 py-1.5 text-base leading-[22px] text-foreground outline-none focus:bg-accent focus:ring-1 focus:ring-input md:text-sm"
    />
  );
}

function Letter({
  preview,
  draft,
  setDraft,
  editable,
}: {
  preview: Extract<Preview, { layout: "email" }>;
  draft: Draft;
  setDraft: (d: Draft) => void;
  editable: boolean;
}) {
  const can = (slot: Exclude<keyof Draft, "typing">) => editable && preview.editable.includes(slot);
  return (
    <div data-approval-letter className="rounded-lg px-4 py-1 ring-1 ring-border ring-inset">
      <Row label="From">
        <span className="min-w-0 truncate">{preview.from ?? "Your account"}</span>
        <span className="hidden text-hint @[480px]:inline">
          · your {preview.thread ? "reply" : "mail"}
        </span>
      </Row>
      {ADDRESS_ROWS.filter((r) => r.shows(can(r.slot), draft[r.slot].length)).map((r) => (
        <Addresses
          key={r.slot}
          label={r.label}
          value={draft[r.slot]}
          onChange={(next, typing) =>
            setDraft({
              ...draft,
              [r.slot]: next,
              typing: { ...draft.typing, [r.slot]: typing ?? draft.typing[r.slot] },
            })
          }
          typing={draft.typing[r.slot]}
          setTyping={(t) => setDraft({ ...draft, typing: { ...draft.typing, [r.slot]: t } })}
          editable={can(r.slot)}
          placeholder="Add someone"
        />
      ))}
      {preview.subject !== null || can("subject") ? (
        <SubjectRow draft={draft} setDraft={setDraft} editable={can("subject")} />
      ) : null}
      <BodyField draft={draft} setDraft={setDraft} editable={can("body")} />
    </div>
  );
}

// ── the other layouts (AP4) ──────────────────────────────────────────────

function EventBody({ p, tz }: { p: Extract<Preview, { layout: "event" }>; tz: string }) {
  // In the event's own timezone, named when it isn't the workspace's (Devin on #491).
  const zone = p.timezone ?? tz;
  const day = dateParts(p.start, zone, p.end);
  return (
    <div className="flex gap-4 rounded-lg bg-secondary/70 p-4">
      {day ? (
        <div className="flex h-14 w-14 shrink-0 flex-col items-center justify-center rounded-lg bg-card ring-1 ring-border">
          <span className="text-[11px] font-semibold tracking-[0.06em] text-copper uppercase">
            {day.month}
          </span>
          <span className="font-display text-[26px] leading-7 text-foreground">{day.date}</span>
        </div>
      ) : null}
      <div className="flex min-w-0 flex-col gap-1">
        <p className="text-base font-medium text-foreground">{p.summary ?? "An event"}</p>
        {day ? <p className="text-meta text-copy">{eventWhen(day.when, zone, tz)}</p> : null}
        {p.where ? <p className="text-meta text-copy">{p.where}</p> : null}
        <p className="text-meta font-medium text-foreground">
          {p.guests.length
            ? `${p.guests.length === 1 ? "Guest" : "Guests"}: ${p.guests.join(", ")}${p.emails_guests ? ", who get Google's invite email" : ""}`
            : "No guests"}
        </p>
      </div>
    </div>
  );
}

function Rows({ rows }: { rows: [string, string][] }) {
  return (
    <div className="rounded-lg bg-secondary/70 px-4 py-1">
      {rows.map(([label, value]) => (
        <div
          key={label}
          className="flex gap-4 border-b border-border py-2.5 text-sm last:border-0 max-[479px]:flex-col max-[479px]:gap-0.5"
        >
          <span className="w-24 shrink-0 text-meta text-hint">{label}</span>
          <span className="min-w-0 break-words whitespace-pre-wrap text-foreground">{value}</span>
        </div>
      ))}
    </div>
  );
}

type Layout<L extends Preview["layout"]> = Extract<Preview, { layout: L }>;
type Pairs = [string, string][];

/** The rows of each read-only layout but the event's (AP4). */
const ROWS: { [L in "share" | "delete" | "routine" | "fields"]: (p: Layout<L>) => Pairs } = {
  share: (p) => [
    ["What", p.what],
    ...(p.who.length ? ([["With", p.who.join(", ")]] as Pairs) : []),
    ...(p.role ? ([["Access", p.role]] as Pairs) : []),
  ],
  delete: (p) => [
    ["Deletes", p.what],
    ...(p.where ? ([["From", p.where]] as Pairs) : []),
    ["Undo", "It can't be undone"],
  ],
  routine: (p) => [
    ["Runs", p.when || "As set"],
    ["Does", p.does],
    ["Tells you", p.texts ? "In Crew, and by text" : "In Crew only"],
  ],
  fields: (p) => p.fields.map((f) => [f.label, f.value]),
};

/** Every layout but the email: read only, Change it to alter (choice 4). */
function OtherBody({ p, tz }: { p: Preview; tz: string }) {
  if (p.layout === "event") return <EventBody p={p} tz={tz} />;
  if (p.layout === "email") return null;
  const rows = (ROWS[p.layout] as (x: Preview) => Pairs)(p);
  return <Rows rows={rows} />;
}

// ── Change it (AP1b) ─────────────────────────────────────────────────────

function ChangeIt({
  agent,
  busy,
  onSend,
  onCancel,
  nothingGoes,
}: {
  agent: string;
  busy: boolean;
  onSend: (note: string) => void;
  onCancel: () => void;
  nothingGoes: string;
}) {
  const [note, setNote] = useState("");
  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        onSend(note);
      }}
    >
      <label htmlFor="approval-change" className="text-sm font-medium text-foreground">
        What should change?
      </label>
      <textarea
        id="approval-change"
        autoFocus
        value={note}
        maxLength={1000}
        onChange={(e) => setNote(e.target.value)}
        rows={3}
        placeholder="Optional: say what to do instead"
        className="rounded-lg bg-card px-3 py-2 text-base leading-[22px] text-foreground ring-1 ring-input outline-none placeholder:text-hint focus:ring-2 focus:ring-ring md:text-sm"
      />
      <div className="flex items-center gap-2">
        <Button type="submit" disabled={busy} className="max-md:h-11 max-md:flex-1">
          Send to {agent}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel} className="max-md:h-11">
          Cancel
        </Button>
      </div>
      <p className="text-xs text-hint">{nothingGoes}</p>
    </form>
  );
}

// ── the open card ────────────────────────────────────────────────────────

function useActions(a: Approval) {
  const refresh = useRefresh(a);
  const yes = useMutation({
    mutationFn: (edits?: EmailEdits) => approve(a.id, edits),
    onSettled: refresh,
  });
  const no = useMutation({
    mutationFn: (note: string) => decline(a.id, note.trim() || null),
    onSettled: refresh,
  });
  return { yes, no, busy: yes.isPending || no.isPending, error: yes.error ?? no.error };
}

/** The eyebrow, and beside it who it's for or since when. */
function CardHeader({
  a,
  mine,
  compact,
  tz,
}: {
  a: Approval;
  mine: boolean;
  compact: boolean;
  tz: string;
}) {
  const since = compact
    ? shortTime(a.created_at, tz)
    : mine
      ? ""
      : `since ${shortTime(a.created_at, tz)}`;
  return (
    <header className="flex items-baseline justify-between gap-3">
      <span className={EYEBROW}>
        {eyebrowOf(a)}
        {mine ? "" : ` · Waiting on ${firstName(a.approver.name)}`}
      </span>
      <span className="shrink-0 text-xs text-hint">{since}</span>
    </header>
  );
}

/** AP1c: the approver changed the letter, and can take it back. */
function EditedBar({ onUndo }: { onUndo: () => void }) {
  return (
    <div className="flex items-center gap-2 text-meta text-copy">
      <Pencil className="size-3.5 text-copper" aria-hidden />
      <span data-approval-edited>Edited by you</span>
      <button
        type="button"
        onClick={onUndo}
        className="ml-1 inline-flex items-center gap-1 font-medium text-foreground underline decoration-foreground/30 underline-offset-[3px] hover:decoration-foreground"
      >
        <Undo2 className="size-3.5" aria-hidden /> Undo my edits
      </button>
    </div>
  );
}

/** Approve (with the letter's edits) and Change it, with the note from 480 px. */
function ApproveRow({
  a,
  busy,
  pending,
  onApprove,
  onChange,
}: {
  a: Approval;
  busy: boolean;
  pending: boolean;
  onApprove: () => void;
  onChange: () => void;
}) {
  return (
    <div className="flex items-center gap-3 @[480px]:border-t @[480px]:border-border @[480px]:pt-4">
      <Button onClick={onApprove} disabled={busy} className="max-md:h-11 max-md:flex-1">
        {pending ? <Loader2 className="animate-spin" /> : <Check />}
        {approveLabel(a)}
      </Button>
      <Button variant="outline" onClick={onChange} disabled={busy} className="max-md:h-11">
        Change it
      </Button>
      <span className="ml-auto hidden text-xs text-hint @[480px]:block">{footNote(a)}</span>
    </div>
  );
}

/** The letter's draft, when it's an email, and what's been changed in it. */
function useDraft(p: Preview) {
  const held = p.layout === "email" ? draftOf(p) : null;
  const [draft, setDraft] = useState<Draft | null>(held);
  const edits = held && draft ? editsOf(held, draft) : undefined;
  return { draft, setDraft, edits, undo: () => setDraft(held) };
}

/** The letter for an email, else the layout's rows. */
function CardBody({
  p,
  draft,
  setDraft,
  editable,
  tz,
}: {
  p: Preview;
  draft: Draft | null;
  setDraft: (d: Draft) => void;
  editable: boolean;
  tz: string;
}) {
  if (p.layout === "email" && draft) {
    return <Letter preview={p} draft={draft} setDraft={setDraft} editable={editable} />;
  }
  return <OtherBody p={p} tz={tz} />;
}

function ErrorLine({ error }: { error: unknown }) {
  if (!error) return null;
  return (
    <p role="alert" className="text-meta text-brick">
      {errorHint(error)}
    </p>
  );
}

/** The card's title (who an edited To now names) and why the crew drafted it. */
function CardTitle({ p, edits, why }: { p: Preview; edits: EmailEdits | undefined; why: string }) {
  return (
    <div className="flex flex-col gap-1">
      <h3 className="text-lg leading-6 font-medium tracking-[-0.01em] text-foreground">
        {titleWith(p, edits?.to)}
      </h3>
      <p className="text-sm leading-[21px] text-copy">{why}</p>
    </div>
  );
}

function OpenCard({ a, me, compact }: { a: Approval; me: Me; compact: boolean }) {
  const p = a.preview;
  const mine = a.approver.id === me.user.id;
  const [changing, setChanging] = useState(false);
  const { draft, setDraft, edits, undo } = useDraft(p);
  const { yes, no, busy, error } = useActions(a);
  const tz = me.workspace.timezone;
  const editable = mine && !changing;
  const actions = changing ? (
    <ChangeIt
      agent={a.agent?.name ?? "Chief of Staff"}
      busy={busy}
      onSend={(note) => no.mutate(note)}
      onCancel={() => setChanging(false)}
      nothingGoes={`Nothing is ${a.kind === "routine" ? "started" : "sent"}. You'll get a new draft.`}
    />
  ) : (
    <ApproveRow
      a={a}
      busy={busy}
      pending={yes.isPending}
      onApprove={() => yes.mutate(edits)}
      onChange={() => setChanging(true)}
    />
  );
  return (
    <div data-approval-card={mine ? "open" : "waiting"} className="@container w-full max-w-[652px]">
      <div className={SHELL}>
        <CardHeader a={a} mine={mine} compact={compact} tz={tz} />
        <CardTitle p={p} edits={edits} why={a.why} />
        <CardBody p={p} draft={draft} setDraft={setDraft} editable={editable} tz={tz} />
        {edits ? <EditedBar onUndo={undo} /> : null}
        {mine ? actions : null}
        <ErrorLine error={error} />
      </div>
    </div>
  );
}

// ── one line, once it's moving or closed (AP2) ───────────────────────────

const LINE_ICONS: Record<string, ReactNode> = {
  running: <Loader2 className="size-[15px] animate-spin text-hint" />,
  done: <Check className="size-[15px] text-moss" />,
  failed: <CircleAlert className="size-[15px] text-brick" />,
  unknown: <AlertTriangle className="size-[15px] text-copper" />,
  declined: <Pencil className="size-[15px] text-hint" />,
  withdrawn: <Undo2 className="size-[15px] text-hint" />,
  expired: <Clock className="size-[15px] text-hint" />,
};

function ClosedLine({ a, me }: { a: Approval; me: Me }) {
  const [open, setOpen] = useState(false);
  const { text, meta } = closedLine(a, me.workspace.timezone);
  const p = a.preview;
  const seeable = p.layout === "email" && a.status !== "declined" && a.status !== "withdrawn";
  return (
    <div className="flex w-full max-w-[652px] flex-col gap-2">
      <div
        data-approval-card={a.status}
        className="flex min-h-11 w-full items-center gap-2.5 rounded-[10px] px-4 py-2.5 shadow-[inset_0_0_0_1px_var(--color-border)]"
      >
        <span aria-hidden className="shrink-0">
          {LINE_ICONS[a.status]}
        </span>
        <span className="min-w-0 flex-1 text-sm leading-[18px] text-copy">{text}</span>
        {seeable ? (
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            className="-my-2 -mr-2 inline-flex h-11 shrink-0 items-center gap-1.5 rounded-md px-2 text-meta text-hint hover:bg-accent hover:text-foreground"
          >
            <Mail className="size-3.5" aria-hidden />
            {open ? "Hide" : a.edited ? "See what was sent" : "See the email"}
            <span className="max-[479px]:hidden">· {meta}</span>
          </button>
        ) : (
          <span className="shrink-0 text-meta text-hint">{meta}</span>
        )}
      </div>
      {open && p.layout === "email" ? (
        <Letter preview={p} draft={draftOf(p)} setDraft={() => {}} editable={false} />
      ) : null}
    </div>
  );
}

/**
 * An `approval_card` message, or a Needs you item (`compact`): live from the
 * approval, else its title alone while the thread loads it.
 */
export function ApprovalCard({
  approval,
  fallbackTitle,
  me,
  compact = false,
}: {
  approval: Approval | undefined;
  fallbackTitle: string;
  me: Me;
  compact?: boolean;
}) {
  if (!approval) {
    return (
      <div data-approval-card="loading" className={cn(SHELL, "w-full max-w-[652px]")}>
        <span className={EYEBROW}>Approve</span>
        <div className="text-lg leading-6 font-medium text-foreground">{fallbackTitle}</div>
      </div>
    );
  }
  if (approval.status === "open") return <OpenCard a={approval} me={me} compact={compact} />;
  return <ClosedLine a={approval} me={me} />;
}

/**
 * An approval's two buttons on its own, for a row that says the rest (Today's
 * Recent, slice 4b §5): Approve runs it as the card's does; Change it opens the chat.
 */
export function ApprovalActions({ a, openChat }: { a: Approval; openChat: () => void }) {
  const { yes, busy, error } = useActions(a);
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          onClick={() => yes.mutate(undefined)}
          disabled={busy}
          className="h-11 md:h-8"
        >
          {yes.isPending ? <Loader2 className="animate-spin" /> : <Check />}
          {approveLabel(a)}
        </Button>
        <Button size="sm" variant="outline" onClick={openChat} className="h-11 md:h-8">
          Change it
        </Button>
      </div>
      {error ? (
        <p role="alert" className="text-meta text-brick">
          {errorHint(error)}
        </p>
      ) : null}
    </div>
  );
}
